import { defineStore } from 'pinia';
import {
  createContractsIndexedByIDObject,
  getOnlyNewOrUpdatedContracts,
} from '@/modules/people/composables/people-edit-pay-detail-item';
import getModuleSetupPayrolls from '@/modules/people/store/root/get-module-setup-payrolls';
import { MODULE_TABS } from '@/modules/people/definitions';
import usePeopleStore from '@/modules/people/store';
import { getRouter } from '@/router/helpers';
import peopleTeamSetup from '@/lib/request/webapp/people/team-setup';
import employmentConditionGet from '@/lib/request/webapp/people/employment-condition-get';
import customFieldsConfigGet from '@/lib/request/webapp/people/custom-fields-config-get';
import useWebappStore from '@webapp-next/store';
import checkFF from '@webapp-next/lib/feature-flag';
import getInstallationCountry from '@/lib/facades/get-installation-country';
import getCountries from '@/lib/facades/get-countries';
import peopleListColumnSettingsGet from '@/lib/request/webapp/people/people-list-column-settings-get';
import {
  LIST_COLUMNS,
  LIST_COLUMNS_DEFAULT_VISIBLE,
  ListColumnsKeys,
  ListColumnsNames,
} from '@/modules/people/definitions/people-list';
import checkListColumnPermissionsByColumnIndex from '@/modules/people/composables/check-list-column-permission-by-column-index';
import {
  ModuleTabs,
  ModuleTabsNav,
  ModuleTabsSetTab,
} from '@/modules/people/types/module-tabs';

import {
  CustomField,
  Department,
  LeaveRule,
  Role,
  StandardHour,
  StressProfile,
  Training,
  Workplace,
} from '@/modules/people/types/people-setup';
import Employee, { EmployeeContract } from '@/modules/people/types/employee';
import BulkActions from '@/modules/people/types/bulk-actions';
import { SearchFilters } from '@/lib/request/webapp/people/employees-get';
import { Country } from '@/types/store';
import { EmployeeContractEmploymentTerm } from '@/modules/people/types/employment';
import { HelperEmptyObject } from '@/types';
import useEmploymentStore from '@/modules/people/store/employment';
import getVueEventBus from '@/lib/vue-event-bus';
import PEOPLE_EVENTS from '@/modules/people/events';
import { ddLogError } from '@/lib/utils/dd-log';
import payRuleContractsGet, {
  PayRuleContractsResponse,
} from '@/lib/request/webapp/people/pay-rule-contracts-get';

type RootStateSetupPermissions =
  | 'CanAdmin'
  | 'CanAdvise'
  | 'CanApproveLeavePay'
  | 'CanApproveLeaveTime'
  | 'CanApproveTimesheet'
  | 'CanEditEmployee'
  | 'CanJournal'
  | 'CanMakeAnnouncement'
  | 'CanRoster'
  | 'CanViewEmployee'
  | 'CanViewPayDetails';

export type RootStateSetupCustomFields = Record<
  CustomField['DeputyField'],
  CustomField
>;

interface RootStateSetup {
  customFields: RootStateSetupCustomFields;
  customFieldsSorted: string[];
  loadingPayrollEnabled: boolean;
  contractsIndexedByID: Record<number, EmployeeContract>;
  EmploymentTermArray: EmployeeContractEmploymentTerm[];
  PayRuleContract: PayRuleContractsResponse;
  WorkplaceArray: Workplace[];
  StandardHourArray: StandardHour[];
  TrainingModuleArray: Training[];
  TrainingModuleArrayIndexed: Record<number, Training>;
  LeaveRuleArray: LeaveRule[];
  LeaveRuleArrayIndexed: Record<number, LeaveRule>;
  JournalCategoryArray: [];
  RoleArray: Role[];
  RoleArrayIndexed: Record<number, Role>;
  DepartmentArray: Department[];
  StressProfileArray: StressProfile[];
  StressProfileArrayIndexed: Record<number, StressProfile>;
  // permissions
  CanAdmin: boolean;
  CanAdvise: boolean;
  CanApproveLeavePay: boolean;
  CanApproveLeaveTime: boolean;
  CanApproveTimesheet: boolean;
  CanEditEmployee: boolean;
  CanJournal: boolean;
  CanMakeAnnouncement: boolean;
  CanRoster: boolean;
  CanViewEmployee: boolean;
  CanViewPayDetails: boolean;
  payrollEnableByLocationId: Record<Workplace['Id'], string[]>;
  payrollEnabled?: Record<string, boolean>;
}

export type RootStateAreasIndexed = Record<number, Department>;
export type RootStateListColumns = Record<ListColumnsNames, boolean>;
export type RootStateListColumnsDisabled = Record<ListColumnsKeys, boolean>;

export type RootStateIntegrationMappingDataItem = HelperEmptyObject & {
  provider: string;
};

export type RootStateIntegrationMappingDataItems = Map<
  string,
  RootStateIntegrationMappingDataItem
>;

interface RootState {
  setup: RootStateSetup;
  areas: Department[]; // TODO: verify the usage of this var because is doubling up
  areasIndexed: RootStateAreasIndexed;
  areaIdsByLocationId: Record<number, number[]>;
  locations: Workplace[]; // TODO: verify the usage of this var because is doubling up
  locationsIndexed: Record<number, Workplace>;
  loading: boolean; // loading state for the essential data
  installationCountry: Country;

  // TODO: create Shift type
  peopleIndexedShifts: Record<number, unknown[]>; // should we cache it in?
  // TODO: create Timesheets type
  peopleIndexedTimesheets: Record<number, unknown[]>; // should we cache it in?

  peopleFormTab: ModuleTabs;
  peopleFormTabSub: string;
  peopleFormIsEdit: boolean;

  // bulk actions
  bulkAction?: BulkActions | false;
  bulkUniqueEmployee: Employee | undefined;
  bulkSelectedEmployeesCountIndexedByLeaveType: Record<string, unknown>; // cache in for bulk action

  // people list
  peopleListColumns: RootStateListColumns;
  peopleListColumnsDisabled: RootStateListColumnsDisabled;

  // TODO: get this function a better naming, it belongs to Integrations IntegrationMappingData
  mappingDataItems: RootStateIntegrationMappingDataItems;

  filters: SearchFilters;

  isEnterprisePayDetailFormDirtyFlag: boolean;
  saveEnterprisePayDetailUpdate: boolean;
  isRoleRefreshListenerInitialized: boolean;
}

function getDefaultFilters(): SearchFilters {
  return {
    company: '-1',
    active: '1', // -1 ALL | 0 ARCHIVED |1 EMPLOYED
    status: '-1',
    payrollid: '-1',
    role: '-1',
    trainingexpiry: '-1',
    training: '',
    leavebalance: [],
    stressprofile: '-1',
    device: '-1',
    twoFactor: '-1',
    onboardingStatus: [],
    employeeIdsUsingContracts: [],
  };
}

function getActiveFilters(_filters: SearchFilters): SearchFilters {
  const filters = JSON.parse(JSON.stringify(_filters));

  Object.keys(filters).forEach(key => {
    if (filters[key] === '-1' || filters[key] === '') {
      delete filters[key];
    }

    if (Array.isArray(filters[key]) && !filters[key].length) {
      delete filters[key];
    }
  });

  return filters;
}

const useRootStore = defineStore('webapp:people:root', {
  state: (): RootState => {
    return {
      setup: {
        customFields: {},
        customFieldsSorted: [],
        payrollEnabled: undefined,
        loadingPayrollEnabled: true,
        contractsIndexedByID: {},
        EmploymentTermArray: [],
        PayRuleContract: {
          data: [],
          nextPageToken: undefined,
          totalCount: 0,
        } as PayRuleContractsResponse,
        WorkplaceArray: [],
        StandardHourArray: [],
        TrainingModuleArray: [],
        TrainingModuleArrayIndexed: {},
        LeaveRuleArray: [],
        LeaveRuleArrayIndexed: {},
        JournalCategoryArray: [],
        RoleArray: [],
        RoleArrayIndexed: {},
        DepartmentArray: [],
        StressProfileArray: [],
        StressProfileArrayIndexed: {},
        CanAdmin: false,
        CanAdvise: false,
        CanApproveLeavePay: false,
        CanApproveLeaveTime: false,
        CanApproveTimesheet: false,
        CanEditEmployee: false,
        CanJournal: false,
        CanMakeAnnouncement: false,
        CanRoster: false,
        CanViewEmployee: false,
        CanViewPayDetails: false,
        payrollEnableByLocationId: {},
      },
      areas: [], // TODO: verify the usage of this var because is doubling up
      areasIndexed: {},
      areaIdsByLocationId: {},
      locations: [], // TODO: verify the usage of this var because is doubling up
      locationsIndexed: {},
      loading: true, // loading state for the essential data
      installationCountry: {} as Country,

      peopleIndexedShifts: {}, // should we cache it in?
      peopleIndexedTimesheets: {}, // should we cache it in?

      peopleFormTab: MODULE_TABS.PERSONAL,
      peopleFormTabSub: '',
      peopleFormIsEdit: false,

      // bulk actions
      bulkAction: undefined,
      bulkUniqueEmployee: undefined,
      bulkSelectedEmployeesCountIndexedByLeaveType: {}, // cache in for bulk action

      // people list
      peopleListColumns: {} as RootStateListColumns,
      peopleListColumnsDisabled: {} as RootStateListColumnsDisabled,

      // TODO: get this function a better naming, it belongs to Integrations IntegrationMappingData
      mappingDataItems: new Map(),

      // filters
      filters: getDefaultFilters(),
      isEnterprisePayDetailFormDirtyFlag: false,
      saveEnterprisePayDetailUpdate: false,

      // listeners
      isRoleRefreshListenerInitialized: false,
    };
  },
  actions: {
    async fetchContracts(): Promise<void> {
      const contracts = await getOnlyNewOrUpdatedContracts();

      this.setContractsIndexedByID(
        createContractsIndexedByIDObject(
          contracts,
          this.getContractsIndexedByID()
        )
      );
    },
    setContractsIndexedByID(contractsIndexedByID = {}): void {
      this.setup.contractsIndexedByID = contractsIndexedByID;
    },
    getContractsIndexedByID(): Record<string, EmployeeContractEmploymentTerm> {
      return this.setup.contractsIndexedByID || {};
    },
    async fetchSetup(): Promise<void> {
      const webappStore = useWebappStore();
      const employmentState = useEmploymentStore();
      this.loading = true;

      try {
        const [peopleSetup] = await Promise.all([
          peopleTeamSetup(),
          this.fetchCustomFields(),
          webappStore.fetchSetup(),
        ]);

        const { data } = peopleSetup;
        const { setup } = data;

        this.setSetup(setup);

        this.setLocations(setup?.WorkplaceArray);
        this.setAreas(setup?.DepartmentArray);
        this.setRoles(setup.RoleArray);
        this.setLeaveRules(setup.LeaveRuleArray);
        this.setTraining(setup.TrainingModuleArray);
        this.setStressProfiles(setup.StressProfileArray);
        this.getModuleSetupPayrolls();

        const installationCountryCode = getInstallationCountry();
        this.installationCountry =
          getCountries().find(
            country => country.Code === installationCountryCode
          ) || ({} as Country);
        await employmentState.fetchEndEmploymentEnabled();
      } catch (e) {
        // TODO: add error to the errors stack like this.errors['webapp:people:store:setup'] = 'Module setup failed to run';
        console.error(e);
      }

      this.loading = false;
      this.initializeRoleRefreshListener();
    },
    initializeRoleRefreshListener(): void {
      if (this.isRoleRefreshListenerInitialized) {
        return;
      }

      const eventBus = getVueEventBus();
      eventBus.$on(PEOPLE_EVENTS.PEOPLE_ROLES_REFRESH, async () => {
        try {
          const { data } = await peopleTeamSetup();
          this.setRoles(data.setup.RoleArray);
        } catch (e) {
          ddLogError('Failed to refresh role list', {
            context: 'people-root-store',
            metadata: {
              event: PEOPLE_EVENTS.PEOPLE_ROLES_REFRESH,
              error: e,
            },
          });
        }
      });

      this.isRoleRefreshListenerInitialized = true;
    },
    getModuleSetupPayrolls,
    getTraining(): Training[] {
      return this.setup.TrainingModuleArray || [];
    },
    setTraining(trainings: Training[] = []): void {
      this.setup.TrainingModuleArray = trainings;
      this.setup.TrainingModuleArrayIndexed = trainings.reduce(
        (reduced: Record<number, Training>, training) => {
          reduced[training.Id] = training;
          return reduced;
        },
        {}
      );

      this.loading = false;
    },
    moduleNav({
      module = MODULE_TABS.PERSONAL,
      edit = false,
      subModule = '',
      dontTriggerRoute = false,
    }: ModuleTabsNav): void {
      const peopleStore = usePeopleStore();
      this.peopleFormTab = module;
      this.peopleFormIsEdit = edit;
      this.peopleFormTabSub = subModule;

      if (!dontTriggerRoute) {
        getRouter()?.pushDontTrigger(
          `#team/${peopleStore.employee.Id}/${
            edit ? 'edit/' : ''
          }${module}/${subModule}`
        );
      }
    },
    setTab({
      tab = MODULE_TABS.PERSONAL,
      subTab = '',
    }: ModuleTabsSetTab | HelperEmptyObject = {}): void {
      this.peopleFormTab = tab;
      this.peopleFormTabSub = subTab;
    },
    getAreas(): Department[] {
      return this.setup.DepartmentArray || [];
    },
    setAreas(areas: Department[] = []): void {
      this.areas = this.setup.DepartmentArray = areas;
      const areasIndexed: RootStateAreasIndexed = {};
      const areaIdsByLocationId: Record<number, number[]> = {};
      for (const area of this.areas) {
        areasIndexed[area.Id] = area;
        const locId = area.Company;
        if (!areaIdsByLocationId[locId]) {
          areaIdsByLocationId[locId] = [];
        }
        areaIdsByLocationId[locId].push(area.Id);
      }
      this.areasIndexed = areasIndexed;
      this.areaIdsByLocationId = areaIdsByLocationId;
    },
    getAreaById(id: number): Department {
      return this.areasIndexed[id] || {};
    },
    setAreaById(id = 0, area: Department = {} as Department): void {
      if (checkFF('pre-6114-employment-collapse')) {
        const oldArea = this.areasIndexed[id];
        this.areasIndexed[id] = area;

        if (oldArea && oldArea.Company !== area.Company) {
          const oldList = this.areaIdsByLocationId[oldArea.Company];
          if (oldList) {
            const idx = oldList.indexOf(id);
            if (idx !== -1) {
              oldList.splice(idx, 1);
            }
          }
        }
        if (!oldArea) {
          const locId = area.Company;
          if (!this.areaIdsByLocationId[locId]) {
            this.areaIdsByLocationId[locId] = [];
          }
          this.areaIdsByLocationId[locId].push(id);
        } else if (oldArea.Company !== area.Company) {
          const locId = area.Company;
          if (!this.areaIdsByLocationId[locId]) {
            this.areaIdsByLocationId[locId] = [];
          }
          this.areaIdsByLocationId[locId].push(id);
        }
      } else {
        this.areasIndexed[id] = area;
      }
    },
    getAreaIds(): RootStateAreasIndexed {
      return this.getAreas().reduce((reduced: RootStateAreasIndexed, area) => {
        reduced[area.Id] = area;
        return reduced;
      }, {});
    },
    getAreaIdsByLocationId(locationIds: number[]): number[] {
      if (checkFF('pre-6114-employment-collapse')) {
        const result: number[] = [];
        const uniqueLocationIds = new Set(locationIds);
        for (const locId of uniqueLocationIds) {
          const areaIds = this.areaIdsByLocationId[locId];
          if (areaIds) {
            result.push(...areaIds);
          }
        }
        return result;
      }
      const areas = this.getAreas();
      return areas
        .filter(area => locationIds.includes(area.Company))
        .map(area => area.Id);
    },
    getMyLocationIds(): number[] {
      return this.getLocations().reduce(
        (reduced: number[], workplace: Workplace) => {
          if (workplace.IsMyWorkplace) {
            reduced.push(workplace.Id);
          }

          return reduced;
        },
        []
      );
    },
    getLocationsById(locationId: number): Workplace {
      return this.locationsIndexed[locationId] || {};
    },
    getLocations(): Workplace[] {
      return this.setup?.WorkplaceArray || [];
    },
    setLocations(locations: Workplace[] = []): void {
      this.locations = this.setup.WorkplaceArray = locations;
      this.locationsIndexed = this.locations.reduce(
        (reduced: Record<number, Workplace>, location: Workplace) => {
          reduced[location.Id] = location;
          return reduced;
        },
        {}
      );
    },
    getLocationById(id: number): Workplace | undefined {
      return this.getLocations().find(
        (workplace: Workplace) => workplace.Id === id
      );
    },
    getTrainingById(id: number): Training | undefined {
      return this.getTraining().find((item: Training) => {
        return item.Id == id;
      });
    },
    getContractById(
      contractId: number | string
    ): EmployeeContractEmploymentTerm | undefined {
      return this.getContracts().find(term => term.Id === contractId);
    },
    setPeopleFormIsEdit(isEdit = false): void {
      this.peopleFormIsEdit = isEdit;
    },
    getPeopleFormIsEdit(): boolean {
      return this.peopleFormIsEdit;
    },
    getRoleById(id = -1): Role {
      return this.setup.RoleArrayIndexed[id] || {};
    },
    getRoles(): Role[] {
      return this.setup.RoleArray || [];
    },
    // empty employee object means reset the bulk action form cache
    setBulkUniqueEmployee(employee?: Employee): void {
      this.bulkUniqueEmployee = employee;
    },
    setBulkAction(action: BulkActions | false): void {
      this.bulkAction = action;

      if (action === false) {
        this.setBulkUniqueEmployee();
      }
    },
    async fetchPeopleListColumns(): Promise<void> {
      try {
        const { data } = await peopleListColumnSettingsGet();
        LIST_COLUMNS_DEFAULT_VISIBLE.forEach(column => {
          if (!(column in data)) {
            data[column] = true; // set default visible columns
          }
        });
        this.setPeopleListColumns(data);
      } catch (e) {
        this.setPeopleListColumns();
      }

      this.setPeopleListColumnsDisabled(
        (Object.keys(LIST_COLUMNS) as Array<ListColumnsKeys>).reduce<
          RootStateListColumnsDisabled
        >((reduced, key) => {
          if (LIST_COLUMNS[key]) {
            reduced[key] = !checkListColumnPermissionsByColumnIndex(
              LIST_COLUMNS[key]
            );
          }

          return reduced;
        }, {} as RootStateListColumnsDisabled)
      );
    },
    setPeopleListColumn(column: ListColumnsNames, value: boolean): void {
      this.peopleListColumns[column] = value;
    },
    setPeopleListColumns(
      columns: RootStateListColumns = {} as RootStateListColumns
    ): void {
      this.peopleListColumns = columns;
    },
    setPeopleListColumnsDisabled(
      columns: RootStateListColumnsDisabled = {} as RootStateListColumnsDisabled
    ): void {
      this.peopleListColumnsDisabled = columns;
    },
    getPeopleListColumnsDisabledByIndex(index: ListColumnsKeys): boolean {
      if (!index) {
        return false;
      }

      return this.peopleListColumnsDisabled[index];
    },
    // TODO: get this function a better naming, it belongs to Integrations IntegrationMappingData
    setMappingDataItems(
      data: RootStateIntegrationMappingDataItems = new Map()
    ): void {
      this.mappingDataItems = data;
    },
    setBulkSelectedEmployeesCountIndexedByLeaveType(
      employees: Record<number, boolean> = {}
    ): void {
      this.bulkSelectedEmployeesCountIndexedByLeaveType = employees;
    },
    getLeaveRules(): LeaveRule[] {
      return this.setup.LeaveRuleArray || [];
    },
    setLeaveRules(leaveRules: LeaveRule[] = []): void {
      this.setup.LeaveRuleArray = leaveRules;
      this.setup.LeaveRuleArrayIndexed = leaveRules.reduce(
        (reduced: Record<number, LeaveRule>, rule) => {
          reduced[rule.Id] = rule;
          return reduced;
        },
        {}
      );
    },
    getLeaveRuleById(leaveRuleId: number): LeaveRule {
      return this.setup.LeaveRuleArrayIndexed[leaveRuleId] || {};
    },
    getLeaveRuleUnitTypeById(leaveRuleId = 0): number | null {
      return this.setup.LeaveRuleArrayIndexed[leaveRuleId]?.UnitType || null;
    },
    async updateLeaveRules(): Promise<void> {
      try {
        const { data } = await peopleTeamSetup();
        this.setLeaveRules(data.setup.LeaveRuleArray);
        this.setContracts(data.setup.EmploymentTermArray);
      } catch (e) {
        console.error(e);
        // TODO: what should we do with the error catching
      }
    },
    setStandardHours(hours: StandardHour[] = []): void {
      this.setup.StandardHourArray = hours;
    },
    getStandardHours(): StandardHour[] {
      return this.setup.StandardHourArray || [];
    },
    setContracts(contracts: EmployeeContractEmploymentTerm[] = []): void {
      this.setup.EmploymentTermArray = contracts;
    },
    getContracts(): EmployeeContractEmploymentTerm[] {
      return this.setup.EmploymentTermArray || [];
    },
    // alias
    setEmploymentTerms(terms: EmployeeContractEmploymentTerm[] = []): void {
      this.setContracts(terms);
    },
    // alias
    getEmploymentTerms(): EmployeeContractEmploymentTerm[] {
      return this.getContracts();
    },
    getEmploymentTermById(
      termId: number | string = 0
    ): EmployeeContractEmploymentTerm {
      return (
        this.getContracts().find(contract => {
          return contract.Id === termId;
        }) || ({} as EmployeeContractEmploymentTerm)
      );
    },
    getEmploymentTermByFileName(filename = ''): EmployeeContractEmploymentTerm {
      return (
        this.getContracts().find(contract => {
          return contract.FileName === filename;
        }) || ({} as EmployeeContractEmploymentTerm)
      );
    },
    // Pay Rule Contracts
    setPayRuleContracts(data: PayRuleContractsResponse): void {
      this.setup.PayRuleContract = data;
    },
    getPayRuleContracts(): PayRuleContractsResponse {
      return (
        this.setup.PayRuleContract ||
        ({
          data: [],
          nextPageToken: undefined,
          totalCount: 0,
        } as PayRuleContractsResponse)
      );
    },
    async fetchPayRuleContracts(): Promise<void> {
      try {
        const { data } = await payRuleContractsGet();
        this.setPayRuleContracts(data);
      } catch (e) {
        console.error(e);
      }
    },
    setWorkplaces(workplaces = []): void {
      this.setup.WorkplaceArray = workplaces;
    },
    getWorkplaces(): Workplace[] {
      return this.setup.WorkplaceArray || [];
    },
    async fetchStandardHours(): Promise<void> {
      try {
        const { data } = await employmentConditionGet();
        // TODO: remove this once the apiv2 is migrated to TS
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.setStandardHours(data);
      } catch (e) {
        console.error(e);
      }
    },
    getStressProfiles(): StressProfile[] {
      return this.setup?.StressProfileArray || [];
    },
    getStressProfileById(id = 0): StressProfile {
      if (!id) {
        throw new Error('id is not defined');
      }

      const stressProfile = this.getStressProfiles().find(
        (item: StressProfile) => item.Id === id
      );

      return stressProfile || ({} as StressProfile);
    },
    async fetchCustomFields(): Promise<void> {
      const { data } = await customFieldsConfigGet();
      const customFieldsSorted: Record<string, string[]> = {};

      this.setCustomFields(
        data.reduce(
          (reduced: RootStateSetupCustomFields, field: CustomField) => {
            // the sorting logic has to be in done this way because we could have a field with the same sorting value
            customFieldsSorted[field.SortOrder] =
              customFieldsSorted[field.SortOrder] || [];

            customFieldsSorted[field.SortOrder].push(field.DeputyField);
            reduced[field.DeputyField] = field;
            return reduced;
          },
          {}
        )
      );

      // the sorting logic has to be in done this way because we could have a field with the same sorting value
      // this is a tweak to move the empty sorting fields to first
      const noSortingId = customFieldsSorted[''];
      delete customFieldsSorted[''];

      // the sorting logic has to be in done this way because we could have a field with the same sorting value
      let sortedCustomFields = Object.keys(customFieldsSorted).reduce(
        (reduced: string[], key) => reduced.concat(customFieldsSorted[key]),
        []
      );

      if (noSortingId) {
        sortedCustomFields = noSortingId.concat(sortedCustomFields); // this is a tweak to move the empty sorting fields to first
      }

      this.setCustomFieldsSorted(sortedCustomFields);
    },
    setCustomFields(customFields = {}): void {
      this.setup.customFields = customFields;
    },
    setCustomFieldsSorted(customFieldsSorted: string[] = []): void {
      this.setup.customFieldsSorted = customFieldsSorted;
    },
    getCustomFields(): RootStateSetupCustomFields {
      return this.setup.customFields || {};
    },
    getCustomFieldById(id = ''): CustomField {
      return this.setup.customFields[id];
    },
    getCustomFieldsSorted(): string[] {
      return this.setup.customFieldsSorted || [];
    },
    // used to mock all the setup object in the store
    setSetup(setup: RootStateSetup): void {
      this.setup = { ...this.setup, ...setup };
    },
    setLoading(loading = false): void {
      this.loading = loading;
    },
    // TODO: this will change just the permissions that lives in the setup object
    setModulePermission(
      permission: RootStateSetupPermissions,
      value = false
    ): void {
      this.setup[permission] = value;
    },
    getModulePermission(permission: RootStateSetupPermissions): boolean {
      return !!this.setup[permission];
    },
    setRoles(roles: Role[] = []): void {
      this.setup.RoleArray = roles;
      this.setup.RoleArrayIndexed = roles.reduce(
        (reduced: Record<number, Role>, role) => {
          reduced[role.Id] = role;
          return reduced;
        },
        {}
      );
    },
    setStressProfiles(stressProfiles: StressProfile[] = []): void {
      this.setup.StressProfileArray = stressProfiles;
      this.setup.StressProfileArrayIndexed = stressProfiles.reduce(
        (reduced: Record<number, StressProfile>, stress) => {
          reduced[stress.Id] = stress;
          return reduced;
        },
        {}
      );
    },
    setShiftsByEmployeeId(employeeId: number, shifts = []): void {
      this.peopleIndexedShifts[employeeId] = shifts;
    },
    // TODO: create Shift type
    getShiftsByEmployeeId(employeeId: number): unknown[] {
      return this.peopleIndexedShifts[employeeId] || [];
    },
    // TODO: create Shift type
    getIsShiftsAlreadyCalledByEmployeeId(employeeId: number): boolean {
      return !!this.peopleIndexedShifts[employeeId];
    },
    // TODO: create Timesheet type
    setTimesheetsByEmployeeId(employeeId: number, timesheets = []): void {
      this.peopleIndexedTimesheets[employeeId] = timesheets;
    },
    // TODO: create Timesheet type
    getTimesheetsByEmployeeId(employeeId: number): unknown[] {
      return this.peopleIndexedTimesheets[employeeId] || [];
    },
    getIsTimesheetsAlreadyCalledByEmployeeId(employeeId: number): boolean {
      return !!this.peopleIndexedTimesheets[employeeId];
    },
    // TODO: create JournalCategory type
    getJournalCategories(): unknown[] {
      return this.setup.JournalCategoryArray || [];
    },
    isSetJournalCategories(): boolean {
      return this.getJournalCategories().length > 0;
    },
    saveEnterprisePayDetailChanges(): void {
      this.saveEnterprisePayDetailUpdate = true;
    },
    setEnterprisePayDetailFormAsDirty(): void {
      this.isEnterprisePayDetailFormDirtyFlag = false;
    },
    getEnterprisePayDetailFormAsDirty(): boolean {
      return this.isEnterprisePayDetailFormDirtyFlag;
    },
    setFiltersCompany(filterLocationId = '-1'): void {
      this.filters.company = filterLocationId;
    },
    setFiltersStressProfile(filterStressProfile = '-1'): void {
      this.filters.stressprofile = filterStressProfile;
    },
    setFiltersRole(filterRole = '-1'): void {
      this.filters.role = filterRole;
    },
    setFiltersActive(filterActive = '1'): void {
      this.filters.active = filterActive;
    },
    setFiltersStatus(filterStatus = '-1'): void {
      this.filters.status = filterStatus;
    },

    setFiltersTwoFactor(twoFactor = '-1'): void {
      this.filters.twoFactor = twoFactor;
    },

    setFiltersOnboardingStatus(onboardingStatus = []): void {
      this.filters.onboardingStatus = onboardingStatus;
    },

    setFiltersPayrollid(payrollid = '-1'): void {
      this.filters.payrollid = payrollid;
    },

    setFiltersTraining(training = '-1'): void {
      this.filters.training = training;
    },

    setFiltersTrainingExpiry(trainingexpiry = '-1'): void {
      this.filters.trainingexpiry = trainingexpiry;
    },

    setFiltersLeaveBalance(leavebalance = ['-1']): void {
      this.filters.leavebalance = leavebalance;
    },

    setFiltersEmployeeIdsUsingContracts(
      employeeIdsUsingContracts: Employee['Id'][] = []
    ): void {
      this.filters.employeeIdsUsingContracts = employeeIdsUsingContracts;
    },

    setFilters(filters: SearchFilters): void {
      if (filters) {
        this.filters = filters;
      }
    },

    clearFilters(): void {
      this.setFilters(getDefaultFilters());
    },

    getActiveFilters(): SearchFilters {
      return getActiveFilters(this.filters);
    },
  },
  getters: {
    // TODO: review this getter as it's quite costly.
    activeAreas: (state): Department[] =>
      state.setup.DepartmentArray?.filter((x: Department) => x.Active) || [],
    getActiveFiltersCount(): number {
      return Object.keys(getActiveFilters(this.filters)).length;
    },
    employmentTermIndexedByFileName(
      state
    ): Record<string, EmployeeContractEmploymentTerm> {
      return (
        state.setup.EmploymentTermArray.reduce(
          (reduced: Record<string, EmployeeContractEmploymentTerm>, term) => {
            if (term.FileName) {
              reduced[term.FileName] = term;
            }
            return reduced;
          },
          {}
        ) || {}
      );
    },
  },
});

export default useRootStore;
